import { useEffect, useRef, useState } from 'react'
import { api, ApiError, createOrQueue } from '../api.ts'

interface ClientHit {
  id: string
  name: string
  city: string | null
  nip: string | null
  phone: string | null
}

/**
 * Step 1 of writing an offer (SPEC 5.2): pick a client — or add one on the spot
 * with just a name and a phone. Sending someone to the clients screen to fill in
 * a full record mid-call is how the minute gets lost.
 *
 * Keyboard-first: type, arrow, Enter. The salesperson is on a laptop in an
 * orchard, not at a desk with a mouse.
 */
export function NewQuoteDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (quoteId: string) => void }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<ClientHit[]>([])
  const [active, setActive] = useState(0)
  const [grant, setGrant] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  // True between a keystroke and the search result for it — Enter in that
  // window must not conclude "no such client" and create a duplicate.
  const [pending, setPending] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  async function szukaj(term: string): Promise<ClientHit[]> {
    try {
      const r = await api.get<{ clients: ClientHit[] }>(`/api/clients?search=${encodeURIComponent(term)}`)
      return r.clients.slice(0, 6)
    } catch {
      return []
    }
  }

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([])
      setPending(false)
      return
    }
    setPending(true)
    const t = setTimeout(() => {
      void szukaj(q.trim()).then((h) => {
        setHits(h)
        setActive(0)
        setPending(false)
      })
    }, 130)
    return () => clearTimeout(t)
  }, [q])

  async function start(customerId: string) {
    setBusy(true)
    setError(null)
    try {
      const r = await createOrQueue<{ quote: { id: string } }>('quote', '/api/quotes', { customerId, isGrantOffer: grant })
      if (r.queued) {
        // Offline: the draft header is queued. It can't be filled with machines
        // until it syncs (that needs the server), so we say so and close.
        setInfo('Zapisano lokalnie — szkic oferty pojawi się, gdy wróci sieć.')
        setBusy(false)
      } else {
        onCreated(r.data.quote.id)
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się utworzyć oferty')
      setBusy(false)
    }
  }

  /** Add a client with just a name and start the offer immediately. */
  async function addAndStart() {
    setBusy(true)
    setError(null)
    try {
      const c = await api.post<{ client: { id: string } }>('/api/clients', { name: q.trim() })
      await start(c.client.id)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się dodać klienta')
      setBusy(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (busy) return
      void (async () => {
        // Enter before the search came back: run it now rather than guess.
        const h = pending && q.trim().length >= 2 ? await szukaj(q.trim()) : hits
        if (pending) {
          setHits(h)
          setPending(false)
        }
        if (h[active]) await start(h[active].id)
        else if (h.length === 0 && q.trim().length >= 2) await addAndStart()
      })()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  const nicNieZnaleziono = q.trim().length >= 2 && hits.length === 0 && !pending

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-24" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-2">
          <h2 className="font-bold text-slate-800 mb-1">Nowa oferta</h2>
          <p className="text-xs text-slate-400">Zacznij od klienta. Jeśli go nie ma — wpisz nazwę i naciśnij Enter.</p>
        </div>

        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Klient: nazwa, NIP, miasto, telefon…"
          className="w-full px-4 py-2.5 text-sm border-y border-slate-200 focus:outline-none"
        />

        <div className="max-h-64 overflow-y-auto">
          {hits.map((c, i) => (
            <button
              key={c.id}
              onClick={() => void start(c.id)}
              onMouseEnter={() => setActive(i)}
              disabled={busy}
              className={`w-full text-left px-4 py-2 ${i === active ? 'bg-brand-50' : ''}`}
            >
              <div className="text-sm text-slate-800">{c.name}</div>
              <div className="text-xs text-slate-400">
                {[c.city, c.nip ? `NIP ${c.nip}` : null, c.phone].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}

          {nicNieZnaleziono && (
            <button
              onClick={() => void addAndStart()}
              disabled={busy}
              className="w-full text-left px-4 py-3 bg-brand-50/50 hover:bg-brand-50"
            >
              <div className="text-sm text-brand-800">
                + Dodaj klienta <strong>„{q.trim()}"</strong> i zacznij ofertę
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Resztę danych uzupełnisz później na karcie klienta.</div>
            </button>
          )}
        </div>

        {error && <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-t border-red-200">{error}</div>}
        {info && <div className="px-4 py-2 text-sm text-amber-800 bg-amber-50 border-t border-amber-200">{info}</div>}

        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={grant} onChange={(e) => setGrant(e.target.checked)} />
            Oferta do wniosku o dotację
            <span className="text-slate-400">(pełna specyfikacja, ważność 6 mies., miejsce na pieczątkę)</span>
          </label>
          <span className="text-[11px] text-slate-400">↑↓ Enter Esc</span>
        </div>
      </div>
    </div>
  )
}
